import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import Editor from "@monaco-editor/react";
import { ArrowLeft, Copy, Play, RefreshCcw, TerminalSquare } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { getCodePracticeLanguage } from "../data/codePracticeData";

const Page = styled.main`
  min-height: 100vh;
  padding: 108px 18px 80px;
  color: #e5e7eb;
  background:
    radial-gradient(920px at 12% 8%, rgba(245, 158, 11, 0.12), transparent 56%),
    radial-gradient(820px at 82% 12%, rgba(56, 189, 248, 0.12), transparent 58%),
    #020617;
`;

const Shell = styled.div`
  max-width: 1380px;
  margin: 0 auto;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;
  flex-wrap: wrap;
`;

const BackButton = styled.button`
  min-height: 42px;
  padding: 0 14px;
  border-radius: 14px;
  border: 1px solid rgba(148, 163, 184, 0.14);
  background: rgba(15, 23, 42, 0.56);
  color: #f8fafc;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 800;
`;

const Hero = styled.section`
  padding: 24px 28px;
  border-radius: 28px;
  border: 1px solid rgba(148, 163, 184, 0.14);
  background:
    radial-gradient(circle at top right, ${({ $glow }) => $glow}, transparent 30%),
    linear-gradient(135deg, rgba(10, 16, 28, 0.9), rgba(2, 6, 23, 0.84));
  box-shadow: 0 28px 80px rgba(0, 0, 0, 0.34);
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(56, 189, 248, 0.1);
  border: 1px solid rgba(56, 189, 248, 0.16);
  color: #7dd3fc;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const HeroRow = styled.div`
  margin-top: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const HeroTitle = styled.h1`
  margin: 0;
  color: #f8fafc;
  font-size: clamp(30px, 4vw, 48px);
  line-height: 1.05;
`;

const HeroSub = styled.p`
  margin: 12px 0 0;
  color: #94a3b8;
  font-size: 15px;
  line-height: 1.8;
  max-width: 760px;
`;

const MetaPill = styled.div`
  padding: 10px 13px;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.14);
  background: rgba(15, 23, 42, 0.56);
  color: #cbd5e1;
  font-size: 12px;
  font-weight: 800;
`;

const WorkspaceGrid = styled.section`
  margin-top: 22px;
  display: grid;
  grid-template-columns: minmax(0, 1.08fr) minmax(360px, 0.92fr);
  gap: 18px;

  @media (max-width: 1040px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.section`
  border-radius: 24px;
  border: 1px solid rgba(148, 163, 184, 0.12);
  background: rgba(8, 14, 26, 0.92);
  overflow: hidden;
  min-width: 0;
`;

const PanelToolbar = styled.div`
  padding: 16px 18px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.12);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
`;

const PanelTitle = styled.div`
  color: #f8fafc;
  font-size: 1rem;
  font-weight: 900;
`;

const ToolbarActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const ToolbarButton = styled.button`
  min-height: 40px;
  padding: 0 14px;
  border-radius: 12px;
  border: ${({ $primary }) => ($primary ? "none" : "1px solid rgba(148, 163, 184, 0.14)")};
  background: ${({ $primary }) =>
    $primary
      ? "linear-gradient(135deg, #f59e0b, #38bdf8)"
      : "rgba(15, 23, 42, 0.56)"};
  color: ${({ $primary }) => ($primary ? "#04111f" : "#f8fafc")};
  font-size: 13px;
  font-weight: 900;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const EditorShell = styled.div`
  height: 620px;

  @media (max-width: 1040px) {
    height: 500px;
  }

  @media (max-width: 640px) {
    height: 420px;
  }
`;

const OutputShell = styled.div`
  padding: 20px;
  min-height: 620px;
  background: rgba(2, 6, 23, 0.82);
  display: flex;
  flex-direction: column;

  @media (max-width: 1040px) {
    min-height: 340px;
  }
`;

const OutputTitle = styled.div`
  color: #f8fafc;
  font-size: 14px;
  font-weight: 900;
`;

const OutputHint = styled.p`
  margin: 10px 0 0;
  color: #94a3b8;
  font-size: 13px;
  line-height: 1.7;
`;

const OutputText = styled.pre`
  margin: 18px 0 0;
  flex: 1;
  white-space: pre-wrap;
  word-break: break-word;
  color: #cbd5e1;
  font-size: 13px;
  line-height: 1.75;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
`;

const OutputNote = styled.div`
  margin-top: 16px;
  padding: 14px 16px;
  border-radius: 16px;
  background: rgba(15, 23, 42, 0.52);
  border: 1px solid rgba(148, 163, 184, 0.1);
  color: #94a3b8;
  font-size: 12px;
  line-height: 1.7;
`;

function runJavaScript(code) {
  const logs = [];
  const consoleProxy = {
    log: (...args) => logs.push(args.map((arg) => String(arg)).join(" ")),
    error: (...args) => logs.push(`Error: ${args.map((arg) => String(arg)).join(" ")}`),
  };

  // Monaco workspace supports direct JS practice in-browser for quick logic drills.
  // eslint-disable-next-line no-new-func
  const executor = new Function("console", `"use strict";\n${code}`);
  executor(consoleProxy);

  return logs.length ? logs.join("\n") : "Code executed. No console output.";
}

export default function CodePracticeWorkspacePage() {
  const navigate = useNavigate();
  const { language } = useParams();

  const selectedLanguage = useMemo(
    () => getCodePracticeLanguage(language),
    [language]
  );

  const [code, setCode] = useState("");
  const [output, setOutput] = useState(
    "Write your code on the left and use Run to see the output here."
  );

  useEffect(() => {
    if (!selectedLanguage) return;

    const storageKey = `skilll_code_practice_${selectedLanguage.slug}`;
    const savedCode = localStorage.getItem(storageKey);
    setCode(savedCode || selectedLanguage.starterCode);
    setOutput(
      selectedLanguage.runMode === "browser"
        ? "Write your code on the left and use Run to see the output here."
        : "This workspace is ready for writing and refining logic. Output execution is not enabled in-browser for this language yet."
    );
  }, [selectedLanguage]);

  useEffect(() => {
    if (!selectedLanguage) return;
    localStorage.setItem(`skilll_code_practice_${selectedLanguage.slug}`, code);
  }, [code, selectedLanguage]);

  if (!selectedLanguage) {
    return (
      <Page>
        <Shell>
          <Panel>
            <OutputShell>
              <OutputTitle>Language not found</OutputTitle>
              <OutputHint>
                This code practice page does not exist yet. Pick one from the main code practice menu.
              </OutputHint>
              <ToolbarButton style={{ marginTop: 16, width: "fit-content" }} onClick={() => navigate("/code-practice")}>
                <ArrowLeft size={16} />
                Back to Code Practice
              </ToolbarButton>
            </OutputShell>
          </Panel>
        </Shell>
      </Page>
    );
  }

  const handleRun = () => {
    if (selectedLanguage.runMode !== "browser") {
      setOutput(
        [
          "In-browser execution is not enabled for this language yet.",
          "",
          "You can still use this page as a clean code editor and draft workspace.",
          "For execution, copy the code and run it in your local compiler or IDE.",
        ].join("\n")
      );
      return;
    }

    try {
      setOutput(runJavaScript(code));
    } catch (error) {
      setOutput(`Execution error:\n${error.message}`);
    }
  };

  const handleReset = () => {
    setCode(selectedLanguage.starterCode);
    setOutput("Editor reset to starter code.");
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setOutput("Code copied to clipboard.");
    } catch {
      setOutput("Could not copy code from this browser session.");
    }
  };

  return (
    <Page>
      <Shell>
        <TopBar>
          <BackButton onClick={() => navigate("/code-practice")}>
            <ArrowLeft size={16} />
            Back to Code Practice
          </BackButton>
        </TopBar>

        <Hero $glow={selectedLanguage.glow}>
          <Eyebrow>
            <TerminalSquare size={14} />
            {selectedLanguage.label} workspace
          </Eyebrow>
          <HeroRow>
            <div>
              <HeroTitle>{selectedLanguage.label} code practice</HeroTitle>
              <HeroSub>
                Clean workspace only: code on the left, output on the right. No predefined logic prompts here.
              </HeroSub>
            </div>
            <MetaPill>
              {selectedLanguage.runMode === "browser" ? "Browser run enabled" : "Editor workspace"}
            </MetaPill>
          </HeroRow>
        </Hero>

        <WorkspaceGrid>
          <Panel>
            <PanelToolbar>
              <PanelTitle>{selectedLanguage.label} editor</PanelTitle>
              <ToolbarActions>
                <ToolbarButton onClick={handleCopy}>
                  <Copy size={15} />
                  Copy
                </ToolbarButton>
                <ToolbarButton onClick={handleReset}>
                  <RefreshCcw size={15} />
                  Reset
                </ToolbarButton>
                <ToolbarButton $primary onClick={handleRun}>
                  <Play size={15} />
                  Run
                </ToolbarButton>
              </ToolbarActions>
            </PanelToolbar>

            <EditorShell>
              <Editor
                height="100%"
                language={selectedLanguage.monacoLanguage}
                value={code}
                onChange={(value) => setCode(value || "")}
                theme="vs-dark"
                options={{
                  minimap: { enabled: false },
                  fontSize: 14,
                  wordWrap: "on",
                  automaticLayout: true,
                  padding: { top: 18 },
                  scrollBeyondLastLine: false,
                }}
              />
            </EditorShell>
          </Panel>

          <Panel>
            <PanelToolbar>
              <PanelTitle>Output</PanelTitle>
            </PanelToolbar>
            <OutputShell>
              <OutputTitle>Console / result</OutputTitle>
              <OutputHint>
                Use this panel to view execution output, logs, or workspace notes.
              </OutputHint>
              <OutputText>{output}</OutputText>
              <OutputNote>
                {selectedLanguage.runMode === "browser"
                  ? "JavaScript can run directly here in the browser."
                  : "For Python, Java, C++, and Go, this page currently works as a clean practice editor and output notes panel."}
              </OutputNote>
            </OutputShell>
          </Panel>
        </WorkspaceGrid>
      </Shell>
    </Page>
  );
}
